import { useState, useMemo, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import styles from './styles.module.css';
import { ADDON_PACKAGES } from '../../constants/addonPackages';
import {
  CheckIcon,
  ChevronDown,
  CurrencyDollar,
  InfoCircleIcon,
  LockClosed,
  PlaneShare,
  RocketIcon,
  Users,
} from '../../assets/svgs/icons';
import StripeCheckoutModal from '../StripeCheckoutModal';
import ModalComponent from '../ModalComponent';
import ModalCancelPlan from '../../pages/Settings/ModalCancelPlan';
import {
  getSubscriptionByUser,
  getActiveSubscription,
  cancelStripeSubscription,
  changePlan,
  getPendingChange,
  cancelPendingChange,
} from '../../services/subscriptionService';
import { getPlansBaseValues } from '../../services/planService';
import { notifySuccessCustom, notifyErrorCustom } from '../../utils/notify';
import { savePendingCheckout } from '../../utils/analytics';
import { IntlContext } from '../../contexts/IntlContext/IntlContext';
import { getCurrentSubscription } from '../../utils/subscription';

const CHECKOUT_USER_TABS = {
  PLANS: 'plans',
  PACKAGES: 'packages',
};

const CHECKOUT_USER_PLAN_OLD_PRICES = {
  1: 149.9,
  2: 299.9,
  3: 449.9,
  4: 899.9,
};

const CHECKOUT_USER_PACKAGE_PRICES = {
  addon_10_artes: 49,
  addon_25_artes: 119,
  addon_50_artes: 249,
  addon_100_artes: 499,
};

const CHECKOUT_USER_FEATURED_PLAN_TIER = 3;
const CHECKOUT_USER_FEATURED_PACKAGE = 'addon_25_artes';

function PlanCardSymbol({ iconKey, highlighted }) {
  const iconColor = highlighted ? '#9002CD' : '#7A869A';

  if (iconKey === 'plane') {
    return <PlaneShare color={iconColor} width={18} height={18} />;
  }

  if (iconKey === 'users') {
    return <Users color={iconColor} />;
  }

  if (iconKey === 'rocket') {
    return <RocketIcon color={iconColor} size="18" />;
  }

  return <LockClosed color={highlighted ? '#CB6CFF' : '#8B95A8'} />;
}

function PackageCardSymbol({ highlighted }) {
  return (
    <CurrencyDollar
      color={highlighted ? '#9002CD' : '#7A869A'}
      width={18}
      height={18}
    />
  );
}

function PlansSelectionView({
  showCancelAccount = true,
  showBackButton = true,
  onSuccess = null,
  returnUrl = null,
  isGuest = false,
  allowCurrentPlanSelection = false,
  onSelectFreePlan = null,
  showPackagesTab = false,
  variant = 'default',
  initialTab = CHECKOUT_USER_TABS.PLANS,
  backUrl = '/',
}) {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const { country } = useContext(IntlContext);
  const navigate = useNavigate();
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    tier: null,
    checkoutType: 'subscription',
    packageCode: null,
  });
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [dbPlans, setDbPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingChange, setPendingChange] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openRuleIndex, setOpenRuleIndex] = useState(null);

  const isEnglish = i18n.language?.toLowerCase().includes('en');
  const isSpanish = i18n.language?.toLowerCase().includes('es');
  const _l = (enVal, ptVal, esVal) => {
    if (isEnglish) return enVal;
    if (isSpanish && esVal !== undefined) return esVal;
    return ptVal;
  };
  const isCheckoutUserVariant = variant === 'checkout-user';
  const shouldShowCheckoutHeader = Boolean(userData) && !isGuest;
  const canShowPackagesTab =
    isCheckoutUserVariant && showPackagesTab && Boolean(userData) && !isGuest;
  const checkoutCardsGridClassName =
    activeTab === CHECKOUT_USER_TABS.PACKAGES
      ? styles.checkoutCardsGridPackages
      : isCheckoutUserVariant
        ? styles.checkoutCardsGridPlansInline
        : styles.checkoutCardsGridPlans;

  const formatPrice = (price) => {
    const planValue = parseFloat(price);
    return planValue.toLocaleString(country === 'br' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: country === 'br' ? 'BRL' : 'USD',
    });
  };

  const formatStaticCurrency = (value) =>
    Number(value).toLocaleString(country === 'br' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: country === 'br' ? 'BRL' : 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (userData) {
        const [
          subResponse,
          activeSubscriptionResponse,
          plansResponse,
          pendingChangeResponse,
        ] = await Promise.all([
          getSubscriptionByUser(),
          getActiveSubscription(),
          getPlansBaseValues(country === 'br' ? 'ptBR' : 'en'),
          getPendingChange().catch(() => ({ data: { pendingChange: null } })),
        ]);

        setActiveSubscription(
          activeSubscriptionResponse?.data ?? getCurrentSubscription(subResponse.data),
        );

        if (plansResponse.data) {
          setDbPlans(plansResponse.data);
        }

        if (
          pendingChangeResponse.data &&
          pendingChangeResponse.data.pendingChange
        ) {
          setPendingChange(pendingChangeResponse.data.pendingChange);
        } else {
          setPendingChange(null);
        }
      } else {
        const plansResponse = await getPlansBaseValues(
          country === 'br' ? 'ptBR' : 'en',
        );
        setActiveSubscription(null);
        setPendingChange(null);
        if (plansResponse.data) {
          setDbPlans(plansResponse.data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userData]);

  useEffect(() => {
    if (!canShowPackagesTab && activeTab !== CHECKOUT_USER_TABS.PLANS) {
      setActiveTab(CHECKOUT_USER_TABS.PLANS);
    }
  }, [canShowPackagesTab, activeTab]);

  const formatPendingChangeDate = (effectiveDate) => {
    if (!effectiveDate) {
      return _l('at the end of the current cycle', 'ao final do ciclo atual', 'al final del ciclo actual');
    }

    return new Date(effectiveDate * 1000).toLocaleDateString(
      isEnglish ? 'en-US' : isSpanish ? 'es-ES' : 'pt-BR',
    );
  };

  const currentTier =
    activeSubscription?.Plan?.tier || activeSubscription?.plan?.tier || 0;

  const isSubscriptionExpired = useMemo(() => {
    if (!activeSubscription || currentTier === 0) return false;
    const expiration = activeSubscription.expiration;
    if (!expiration) return false;
    return new Date(expiration) < new Date();
  }, [activeSubscription, currentTier]);

  const plans = useMemo(() => {
    const mapItems = (tier) => {
      const plan = dbPlans.find((p) => p.tier === tier);
      if (!plan || !plan.PlanItems) return [];

      const itemTypes = [
        'single_post_quantity',
        'scheduling_quantity',
        'ia_legend_generative_quantity',
        'post_quantity',
      ];

      return itemTypes
        .map((type) => {
          const item = plan.PlanItems.find(
            (i) =>
              i.type === type ||
              (type === 'single_post_quantity' &&
                i.type === 'art_generation_count'),
          );
          if (!item) return null;

          const featureNames = {
            single_post_quantity: _l('Arts/month', 'Artes/mês', 'Artes/mes'),
            scheduling_quantity: _l('Campaigns/month', 'Campanhas/mês', 'Campañas/mes'),
            ia_legend_generative_quantity: _l('Legends/month', 'Legendas/mês', 'Leyendas/mes'),
            post_quantity: _l('Strategies/month', 'Estratégias/mês', 'Estrategias/mes'),
          };
          const name = featureNames[type] || '';
          return `${name}: ${item.quantity}`;
        })
        .filter(Boolean);
    };

    const freeFeatures = _l(
      [
        { tone: 'positive', text: 'Up to 2 campaigns' },
        { tone: 'positive', text: 'Up to 8 arts in total' },
        { tone: 'positive', text: 'Schedule your own posts' },
        { tone: 'negative', text: 'No monthly renewal' },
      ],
      [
        { tone: 'positive', text: 'Até 2 campanhas' },
        { tone: 'positive', text: 'Até 8 artes no total' },
        { tone: 'positive', text: 'Agendar seus próprios posts' },
        { tone: 'negative', text: 'Sem renovação mensal' },
      ],
      [
        { tone: 'positive', text: 'Hasta 2 campañas' },
        { tone: 'positive', text: 'Hasta 8 artes en total' },
        { tone: 'positive', text: 'Programar tus propias publicaciones' },
        { tone: 'negative', text: 'Sin renovación mensual' },
      ]
    );

    const getPlanFromDb = (tier) => dbPlans.find((p) => p.tier === tier);

    return [
      {
        tier: 0,
        id: 'free',
        name: _l('Free', 'Grátis', 'Gratuito'),
        oldPrice: null,
        price: formatStaticCurrency(0),
        period: t('pricing.period_month'),
        description: t('plansPage.freeVersionDescription'),
        features: freeFeatures,
        priceId: null,
        featured: false,
      },
      {
        tier: 1,
        id: 'essencial',
        name: getPlanFromDb(1)?.description || _l('Essential', 'Essencial', 'Esencial'),
        oldPrice: null,
        price: getPlanFromDb(1)?.price
          ? formatPrice(getPlanFromDb(1).price)
          : formatStaticCurrency(49),
        period: t('pricing.period_month'),
        description: t('plansPage.essentialPlanDescription'),
        features: mapItems(1),
        priceId: getPlanFromDb(1)?.stripePriceId,
        featured: false,
      },
      {
        tier: 2,
        id: 'growth',
        name: getPlanFromDb(2)?.description || 'Growth',
        oldPrice: null,
        price: getPlanFromDb(2)?.price
          ? formatPrice(getPlanFromDb(2).price)
          : formatStaticCurrency(99),
        period: t('pricing.period_month'),
        description: t('plansPage.entrepreneurPlanDescription'),
        features: mapItems(2),
        priceId: getPlanFromDb(2)?.stripePriceId,
        featured: true,
        badge: t('pricing.badge_popular'),
      },
      {
        tier: 3,
        id: 'pro',
        name: getPlanFromDb(3)?.description || 'Pro',
        oldPrice: null,
        price: getPlanFromDb(3)?.price
          ? formatPrice(getPlanFromDb(3).price)
          : formatStaticCurrency(149),
        period: t('pricing.period_month'),
        description: t('plansPage.professionalPlanDescription'),
        features: mapItems(3),
        priceId: getPlanFromDb(3)?.stripePriceId,
      },
      {
        tier: 4,
        id: 'business',
        name: getPlanFromDb(4)?.description || 'Business',
        oldPrice: null,
        price: getPlanFromDb(4)?.price
          ? formatPrice(getPlanFromDb(4).price)
          : formatStaticCurrency(299),
        period: t('pricing.period_month'),
        description: t('plansPage.businessPlanDescription'),
        features: mapItems(4),
        priceId: getPlanFromDb(4)?.stripePriceId,
        featured: false,
      },
    ];
  }, [dbPlans, isEnglish, isSpanish, t]);

  const getPendingPlanName = (targetTier) => {
    if (targetTier === 0) {
      return _l('Free', 'Grátis', 'Gratuito');
    }

    return plans.find((plan) => plan.tier === targetTier)?.name || targetTier;
  };

  const isPendingCancellation = pendingChange?.targetTier === 0;

  const handleSelectPlan = (tier) => {
    if (tier === currentTier && !isSubscriptionExpired) {
      if (allowCurrentPlanSelection && tier === 0 && onSelectFreePlan) {
        onSelectFreePlan();
      }
      return;
    }

    if (tier < currentTier && !isSubscriptionExpired) {
      setPendingTier(tier);
      setIsDowngradeModalOpen(true);
    } else {
      const selectedPlan = plans.find((p) => p.tier === tier)
      const rawPrice = selectedPlan?.price
        ? parseFloat(String(selectedPlan.price).replace(/[^\d,.-]/g, '').replace(',', '.'))
        : 0
      savePendingCheckout({
        name: selectedPlan?.name || '',
        price: rawPrice,
        currency: country === 'br' ? 'BRL' : 'USD',
        checkoutType: 'subscription',
      })
      setModalConfig({
        isOpen: true,
        tier,
        checkoutType: 'subscription',
        packageCode: null,
      });
    }
  };

  const handleSelectPackage = (packageCode) => {
    if (!userData || isGuest) {
      notifyErrorCustom(
        _l(
          'You need to be logged in to buy additional packages.',
          'Você precisa estar logado para comprar pacotes adicionais.',
          'Necesitas iniciar sesión para comprar paquetes adicionales.',
        ),
      );
      return;
    }

    const selectedPkg = ADDON_PACKAGES.find((p) => p.code === packageCode)
    const priceMap = {
      addon_10_artes: 49,
      addon_25_artes: 119,
      addon_50_artes: 249,
      addon_100_artes: 499,
    }
    savePendingCheckout({
      name: selectedPkg?.title || '',
      price: priceMap[packageCode] || 0,
      currency: country === 'br' ? 'BRL' : 'USD',
      checkoutType: 'addon',
      packageCode,
    })

    setModalConfig({
      isOpen: true,
      tier: null,
      checkoutType: 'addon',
      packageCode,
    });
  };

  const handleConfirmDowngrade = async () => {
    if (pendingTier === 0) {
      setIsProcessing(true);
      try {
        const response = await cancelStripeSubscription();
        if (response.responseStatus === 200) {
          notifySuccessCustom(
            _l(
              'Your return to the free plan was scheduled successfully for the end of the current cycle.',
              'Seu retorno ao plano gratuito foi agendado com sucesso para o fim do ciclo atual.',
              'Tu vuelta al plan gratuito se programó correctamente para el final del ciclo actual.',
            ),
          );
          await fetchData();
          if (onSuccess) onSuccess('downgrade');
        } else {
          notifyErrorCustom(
            _l(
              'Error canceling subscription. Please try again.',
              'Erro ao cancelar assinatura. Por favor, tente novamente.',
              'Error al cancelar la suscripción. Por favor, inténtalo de nuevo.',
            ),
          );
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        notifyErrorCustom(
          _l(
            'Error canceling subscription. Please try again.',
            'Erro ao cancelar assinatura. Por favor, tente novamente.',
            'Error al cancelar la suscripción. Por favor, inténtalo de nuevo.',
          ),
        );
      } finally {
        setIsProcessing(false);
      }
    } else {
      setIsProcessing(true);
      try {
        const response = await changePlan(pendingTier);
        if (response.responseStatus === 200) {
          const effectiveDate = formatPendingChangeDate(
            response.data?.effectiveDate,
          );
          const targetPlanName = getPendingPlanName(pendingTier);
          notifySuccessCustom(
            _l(
              `Downgrade scheduled successfully! Your plan will change to ${targetPlanName} on ${effectiveDate}.`,
              `Downgrade agendado com sucesso! Seu plano será alterado para ${targetPlanName} em ${effectiveDate}.`,
              `Degradación programada correctamente. Tu plan cambiará a ${targetPlanName} el ${effectiveDate}.`,
            ),
          );
          await fetchData();
          if (onSuccess) onSuccess('downgrade');
        } else {
          notifyErrorCustom(
            _l(
              'Error scheduling downgrade. Please try again.',
              'Erro ao agendar downgrade. Por favor, tente novamente.',
              'Error al programar la degradación. Por favor, inténtalo de nuevo.',
            ),
          );
        }
      } catch (error) {
        console.error('Error changing plan:', error);
        notifyErrorCustom(
          _l(
            'Error scheduling downgrade. Please try again.',
            'Erro ao agendar downgrade. Por favor, tente novamente.',
            'Error al programar la degradación. Por favor, inténtalo de nuevo.',
          ),
        );
      } finally {
        setIsProcessing(false);
      }
    }
    setIsDowngradeModalOpen(false);
    setPendingTier(null);
  };

  const handleCancelPendingChange = async () => {
    setIsProcessing(true);
    try {
      const response = await cancelPendingChange();
      if (response.responseStatus === 200) {
        notifySuccessCustom(
          isPendingCancellation
            ? _l(
              'Scheduled cancellation canceled successfully.',
              'Cancelamento agendado cancelado com sucesso.',
              'Cancelación programada cancelada correctamente.',
            )
            : _l(
              'Scheduled downgrade canceled successfully.',
              'Downgrade agendado cancelado com sucesso.',
              'Degradación programada cancelada correctamente.',
            ),
        );
        setPendingChange(null);
        await fetchData();
      } else {
        notifyErrorCustom(
          isPendingCancellation
            ? _l(
              'Error canceling scheduled cancellation.',
              'Erro ao cancelar o cancelamento agendado.',
              'Error al cancelar la cancelación programada.',
            )
            : _l(
              'Error canceling scheduled downgrade.',
              'Erro ao cancelar downgrade agendado.',
              'Error al cancelar la degradación programada.',
            ),
        );
      }
    } catch (error) {
      console.error('Error cancelling pending change:', error);
      notifyErrorCustom(
        isPendingCancellation
          ? _l(
            'Error canceling scheduled cancellation.',
            'Erro ao cancelar o cancelamento agendado.',
            'Error al cancelar la cancelación programada.',
          )
          : _l(
            'Error canceling scheduled downgrade.',
            'Erro ao cancelar downgrade agendado.',
            'Error al cancelar la degradación programada.',
          ),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setModalConfig({
      isOpen: false,
      tier: null,
      checkoutType: 'subscription',
      packageCode: null,
    });
  };

  const getButtonText = (plan) => {
    if (
      plan.tier === currentTier &&
      allowCurrentPlanSelection &&
      plan.tier === 0
    ) {
      return _l('Select plan', 'Selecionar plano', 'Seleccionar plan');
    }
    if (plan.tier === currentTier && !isSubscriptionExpired) {
      return _l('Current plan', 'Plano atual', 'Plan actual');
    }
    if (plan.tier === currentTier && isSubscriptionExpired) {
      return _l('Reactivate plan', 'Reativar plano', 'Reactivar plan');
    }
    if (plan.tier === 0 && currentTier > 0) {
      return _l('Cancel subscription', 'Cancelar assinatura', 'Cancelar suscripción');
    }
    if (plan.tier > currentTier) {
      return _l('Upgrade', 'Upgrade', 'Mejorar') + ` ${plan.name}`;
    }
    return _l('Downgrade', 'Downgrade', 'Degradar') + ` ${plan.name}`;
  };

  const getCheckoutUserButtonText = (plan) => {
    if (
      plan.tier === currentTier &&
      allowCurrentPlanSelection &&
      plan.tier === 0
    ) {
      return _l('Select plan', 'Selecionar plano', 'Seleccionar plan');
    }
    if (plan.tier === currentTier && !isSubscriptionExpired) {
      return _l('Your current plan', 'Seu plano atual', 'Tu plan actual');
    }
    if (plan.tier === currentTier && isSubscriptionExpired) {
      return _l('Reactivate plan', 'Reativar plano', 'Reactivar plan');
    }
    if (plan.tier === 0 && currentTier > 0) {
      return _l('Cancel subscription', 'Cancelar assinatura', 'Cancelar suscripción');
    }
    return _l('Select plan', 'Selecionar plano', 'Seleccionar plan');
  };

  const getCardClass = (plan) => {
    const isCurrentPlan = currentTier === plan.tier && !isSubscriptionExpired;
    if (isCurrentPlan && !allowCurrentPlanSelection) {
      return `${styles.card} ${styles.cardCurrent}`;
    }
    if (plan.tier < currentTier) {
      return `${styles.card} ${styles.cardDowngrade}`;
    }
    if (plan.featured) {
      return `${styles.card} ${styles.cardFeatured}`;
    }
    return `${styles.card} ${styles.cardUpgrade}`;
  };

  const getButtonClass = (plan) => {
    const isCurrentPlan = currentTier === plan.tier && !isSubscriptionExpired;
    if (isCurrentPlan && (!allowCurrentPlanSelection || plan.tier !== 0)) {
      return `${styles.selectButton} ${styles.buttonDisabled}`;
    }
    if (currentTier === plan.tier && isSubscriptionExpired) {
      return `${styles.selectButton} ${styles.buttonUpgrade}`;
    }
    if (plan.tier < currentTier) {
      return `${styles.selectButton} ${styles.buttonDowngrade}`;
    }
    if (plan.featured) {
      return `${styles.selectButton} ${styles.buttonFeatured}`;
    }
    return `${styles.selectButton} ${styles.buttonUpgrade}`;
  };

  const paidPlans = plans.filter((plan) => plan.tier > 0);
  const freePlan = plans.find((plan) => plan.tier === 0);

  const checkoutUserCopy = isEnglish
    ? {
      title: 'Create complete campaigns.',
      plansHighlight: 'Produce in minutes what would take weeks.',
      packagesHighlight: 'No confusing briefing. No rework.',
      tabs: {
        plans: 'Plans',
        packages: 'Packages',
      },
      currentBadge: 'Current plan',
      featuredBadge: 'Popular',
      packageButton: 'Select package',
      freeTitle: 'Free Plan',
      freePriceLabel: 'Current access',
      rulesTitle: 'Art and campaign usage and consumption rules',
      securePayment: 'Secure payment processed via',
    }
    : isSpanish
      ? {
        title: 'Crea campañas completas.',
        plansHighlight: 'Elige el plan perfecto para tu negocio.',
        packagesHighlight: 'Elige el paquete perfecto para tu negocio.',
        tabs: {
          plans: 'Planes',
          packages: 'Paquetes',
        },
        currentBadge: 'Plan actual',
        featuredBadge: 'Más popular',
        packageButton: 'Seleccionar paquete',
        freeTitle: 'Plan Gratuito',
        freePriceLabel: 'Acceso actual',
        rulesTitle: 'Reglas de uso y consumo de artes y campañas',
        securePayment: 'Pago seguro realizado a través de',
      }
      : {
        title: 'Crie campanhas completas.',
        plansHighlight: 'Escolha um plano para o seu negócio.',
        packagesHighlight: 'Escolha um pacote para o seu negócio.',
        tabs: {
          plans: 'Planos',
          packages: 'Pacotes',
        },
        currentBadge: 'Plano atual',
        featuredBadge: 'Mais popular',
        packageButton: 'Selecionar pacote',
        freeTitle: 'Plano Grátis',
        freePriceLabel: 'Acesso atual',
        rulesTitle: 'Regras de uso e consumo de artes e campanhas',
        securePayment: 'Pagamento seguro realizado via',
      };

  const checkoutUserRules = isEnglish
    ? [
      {
        title: 'What are arts and how do they work?',
        content:
          'Arts are the visual creatives generated by the platform. You can create them one by one through the Single Art flow, or together through the Campaign flow. In both cases, each generated art counts toward your contracted limit, and that limit is also consumed when you regenerate or edit an existing art.',
      },
      {
        title: 'What are campaigns and how do they work?',
        content:
          'The Campaign flow brings everything together in one place: strategy, art and caption generation, and social network scheduling. To create a campaign, you need at least 5 arts available in your limit, and you can choose to generate sets of 5, 10, 15 or 20 arts. Strategy creation is included in all plans and does not consume any additional limit.',
      },
      {
        title: 'Campaign strategy edits with AI',
        content:
          'The strategy generated for each campaign can also be edited. Manual editing is always free and has no limit. AI editing is allowed up to 2 times per strategy. From the third edit on, you will need to adjust it manually. This limit applies to all plans.',
      },
      {
        title: 'Caption edits with AI',
        content:
          'Every generated art already comes with an AI-generated caption. If you want to change it, you have two options: edit manually, which does not consume any limit; or edit with AI, which counts against your edit balance. Regardless of the plan, each caption can be edited with AI up to 5 times. After that, it can only be edited manually.',
      },
      {
        title: 'What consumes my art limit?',
        content:
          'Your art limit is deducted in three situations: when you generate a new art through the Single Art or Campaign flow; when you regenerate an already created art or reject a campaign to regenerate everything from scratch; and when you edit an art, either individually or in bulk. Manual edits to captions and strategies do not consume this limit.',
      },
    ]
    : isSpanish
      ? [
        {
          title: '¿Qué son las artes y cómo funcionan?',
          content:
            'Las artes son los creativos visuales generados por la plataforma. Puedes crearlos uno por uno mediante el flujo de Arte Único, o en conjunto mediante el flujo de Campañas. En ambos casos, cada arte generado cuenta para tu límite contratado, y ese límite también se consume cuando regeneras o editas un arte existente.',
        },
        {
          title: '¿Qué son las campañas y cómo funcionan?',
          content:
            'El flujo de Campañas lo reúne todo en un solo lugar: estrategia, generación de artes y pies de foto, y programación para redes sociales. Para crear una campaña, necesitas al menos 5 artes disponibles en tu límite, y puedes elegir generar conjuntos de 5, 10, 15 o 20 artes. La creación de la estrategia está incluida en todos los planes y no consume ningún límite adicional.',
        },
        {
          title: 'Ediciones de estrategia de campaña con IA',
          content:
            'La estrategia generada para cada campaña también se puede editar. La edición manual es siempre gratuita y no tiene límite. La edición con IA está permitida hasta 2 veces por estrategia. A partir de la tercera edición, deberás ajustarla manualmente. Este límite se aplica a todos los planes.',
        },
        {
          title: 'Ediciones de pies de foto con IA',
          content:
            'Todo arte generado ya incluye un pie de foto creado automáticamente por IA. Si quieres cambiarlo, tienes dos opciones: editar manualmente, que no consume ningún límite; o editar con IA, que cuenta en tu saldo de ediciones. Independientemente del plan, cada pie de foto se puede editar con IA hasta 5 veces. Después de eso, solo se puede editar manualmente.',
        },
        {
          title: '¿Qué consume mi límite de artes?',
          content:
            'Tu límite de artes se descuenta en tres situaciones: cuando generas un arte nuevo mediante el flujo de Arte Único o Campañas; cuando regeneras un arte ya creado o rechazas una campaña para regenerarlo todo desde cero; y cuando editas un arte, ya sea individualmente o en lote. Las ediciones manuales de pies de foto y estrategias no consumen este límite.',
        },
      ]
      : [
        {
          title: 'O que são artes e como funcionam',
          content:
            'Artes são os criativos visuais gerados pela plataforma. Você pode criá-las uma por vez, pelo fluxo de Arte Única, ou em conjunto, pelo fluxo de Campanhas. Em ambos os casos, cada arte gerada conta no seu limite contratado, e esse limite também é consumido quando você regera ou edita uma arte existente.',
        },
        {
          title: 'O que são campanhas e como funcionam',
          content:
            'O fluxo de Campanhas reúne tudo em um só lugar: estratégia, geração de artes e legendas, e agendamento para redes sociais. Para criar uma campanha, você precisa ter ao menos 5 artes disponíveis no seu limite — e pode escolher gerar conjuntos de 5, 10, 15 ou 20 artes. A criação da estratégia já está inclusa em todos os planos e não consome nenhum limite adicional.',
        },
        {
          title: 'Edições de estratégia de campanha com IA',
          content:
            'A estratégia gerada para cada campanha também pode ser editada. Editar manualmente é sempre livre e não tem limite. Já a edição com IA é permitida até 2 vezes por estratégia. A partir da terceira edição, você precisará ajustar manualmente. Esse limite se aplica a todos os planos.',
        },
        {
          title: 'Edições de legendas com IA',
          content:
            'Toda arte gerada já vem com uma legenda criada automaticamente por IA. Se quiser ajustar, você tem duas opções: editar manualmente, que não consome nenhum limite; ou editar com IA, que conta no seu saldo de edições. Independentemente do plano, cada legenda pode ser editada com IA até 5 vezes. Depois disso, só é possível editar manualmente.',
        },
        {
          title: 'O que consome o meu limite de artes?',
          content:
            'Seu limite de artes é descontado em três situações: quando você gera uma arte nova, pelo fluxo de Arte Única ou de Campanhas; quando você regera uma arte já criada ou reprova uma campanha para regerar tudo do zero; e quando você edita uma arte, seja individualmente ou em lote. Edições manuais de legendas e estratégias não consomem esse limite.',
        },
      ];

  const checkoutUserFreeItems = _l(
    [
      { tone: 'positive', text: 'Up to 2 campaigns' },
      { tone: 'positive', text: 'Up to 8 arts in total' },
      { tone: 'positive', text: 'Schedule your own posts' },
      { tone: 'negative', text: 'No monthly renewal' },
    ],
    [
      { tone: 'positive', text: 'Até 2 campanhas' },
      { tone: 'positive', text: 'Até 8 artes no total' },
      { tone: 'positive', text: 'Agendar seus próprios posts' },
      { tone: 'negative', text: 'Sem renovação mensal' },
    ],
    [
      { tone: 'positive', text: 'Hasta 2 campañas' },
      { tone: 'positive', text: 'Hasta 8 artes en total' },
      { tone: 'positive', text: 'Programar tus propias publicaciones' },
      { tone: 'negative', text: 'Sin renovación mensual' },
    ]
  );

  const getCheckoutUserVisibleFeatures = (features = []) => features.slice(0, 1);

  const checkoutUserPlanCards = useMemo(() => {
    const subtitleByTier = {
      1: t('plansPage.subtitleEssential'),
      2: t('plansPage.subtitleGrowth'),
      3: t('plansPage.subtitleMaster'),
      4: t('plansPage.subtitleBusiness'),
    };

    const iconByTier = {
      1: 'plane',
      2: 'users',
      3: 'rocket',
      4: 'users',
    };

    const imageBankByTier = {
      1: { standard: 5, ia: 10 },
      2: { standard: 10, ia: 20 },
      3: { standard: 20, ia: 40 },
      4: { standard: 40, ia: 80 },
    };

    return paidPlans.map((plan) => ({
      ...plan,
      oldPrice: CHECKOUT_USER_PLAN_OLD_PRICES[plan.tier]
        ? formatStaticCurrency(CHECKOUT_USER_PLAN_OLD_PRICES[plan.tier])
        : null,
      subtitle: subtitleByTier[plan.tier] || plan.description,
      iconKey: iconByTier[plan.tier] || 'plane',
      featuredCard: plan.tier === CHECKOUT_USER_FEATURED_PLAN_TIER,
      currentCard: currentTier === plan.tier && !isSubscriptionExpired,
      imageBank: imageBankByTier[plan.tier] || null,
    }));
  }, [currentTier, isSubscriptionExpired, paidPlans, t]);

  const checkoutUserPackageCards = useMemo(() => {
    return ADDON_PACKAGES.map((pkg) => ({
      ...pkg,
      subtitle: _l('Additional credits', 'Créditos adicionais', 'Créditos adicionales'),
      price: formatStaticCurrency(
        CHECKOUT_USER_PACKAGE_PRICES[pkg.code] ?? Number(pkg.priceLabel || 0),
      ),
      featuredCard: pkg.code === CHECKOUT_USER_FEATURED_PACKAGE,
      features: _l(
        [
          `+${pkg.arts} extra arts`,
          'Added to your current account balance',
          'One-time checkout without changing plan',
        ],
        [
          `+${pkg.arts} artes extras`,
          'Somado ao saldo atual da conta',
          'Compra avulsa sem trocar o plano',
        ],
        [
          `+${pkg.arts} artes extra`,
          'Añadido al saldo actual de la cuenta',
          'Compra única sin cambiar de plan',
        ],
      ),
    }));
  }, [isEnglish, isSpanish]);

  const pendingChangeBanner = pendingChange ? (
    <motion.section
      initial={{ y: -12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={styles.pendingChangeCard}
    >
      <div className={styles.pendingChangeMeta}>
        <span className={styles.pendingChangeEyebrow}>
          {isPendingCancellation
            ? _l('Scheduled cancellation', 'Cancelamento agendado', 'Cancelación programada')
            : _l('Scheduled downgrade', 'Downgrade agendado', 'Degradación programada')}
        </span>
        <h2 className={styles.pendingChangeTitle}>
          {_l('Your current plan remains active until ', 'Seu plano atual segue ativo até ', 'Tu plan actual sigue activo hasta ')}
          {formatPendingChangeDate(pendingChange.effectiveDate)}
        </h2>
        <p className={styles.pendingChangeDescription}>
          {isPendingCancellation
            ? _l(
              'On the next renewal, your subscription will end and your account will return to the Free plan. Until then, you keep access to the current plan resources.',
              'Na próxima renovação, sua assinatura será encerrada e sua conta voltará para o plano Grátis. Até lá, você continua com os recursos do plano atual.',
              'En la próxima renovación, tu suscripción finalizará y tu cuenta volverá al plan Gratuito. Hasta entonces, sigues teniendo acceso a los recursos del plan actual.',
            )
            : _l(
              `On the next renewal, your subscription will change to the ${getPendingPlanName(pendingChange.targetTier)} plan. Until then, you keep access to the current plan resources.`,
              `Na próxima renovação, sua assinatura será alterada para o plano ${getPendingPlanName(pendingChange.targetTier)}. Até lá, você continua com os recursos do plano atual.`,
              `En la próxima renovación, tu suscripción cambiará al plan ${getPendingPlanName(pendingChange.targetTier)}. Hasta entonces, sigues teniendo acceso a los recursos del plan actual.`,
            )}
        </p>
      </div>
      <div className={styles.pendingChangeActions}>
        <button
          className={styles.cancelPendingButton}
          onClick={handleCancelPendingChange}
          disabled={isProcessing}
        >
          {isPendingCancellation
            ? _l('Cancel scheduled cancellation', 'Cancelar cancelamento agendado', 'Cancelar cancelación programada')
            : _l('Cancel scheduled downgrade', 'Cancelar downgrade agendado', 'Cancelar degradación programada')}
        </button>
      </div>
    </motion.section>
  ) : null;

  const expiredBanner = isSubscriptionExpired ? (
    <motion.section
      initial={{ y: -12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={styles.expiredBanner}
    >
      <div className={styles.expiredBannerIcon}>
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="#b42318"
          />
        </svg>
      </div>
      <div className={styles.expiredBannerContent}>
        <h2 className={styles.expiredBannerTitle}>
          {_l('Your subscription expired', 'Sua assinatura expirou', 'Tu suscripción ha expirado')}
        </h2>
        <p className={styles.expiredBannerText}>
          {_l(
            'Your subscription payment was not detected and access to the plan resources has been suspended. Choose a plan below to reactivate your account.',
            'O pagamento da sua assinatura não foi detectado e seu acesso aos recursos do plano foi suspenso. Escolha um plano abaixo para reativar sua conta.',
            'No se detectó el pago de tu suscripción y el acceso a los recursos del plan ha sido suspendido. Elige un plan a continuación para reactivar tu cuenta.',
          )}
        </p>
      </div>
    </motion.section>
  ) : null;

  const checkoutModals = (
    <>
      <StripeCheckoutModal
        isOpen={modalConfig.isOpen}
        onClose={handleCloseModal}
        tier={modalConfig.tier}
        userId={userData?.uuid}
        email={userData?.email}
        returnUrl={returnUrl}
        isGuest={isGuest}
        checkoutType={modalConfig.checkoutType}
        packageCode={modalConfig.packageCode}
      />

      <ModalComponent
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
      >
        <ModalCancelPlan onClose={() => setIsCancelModalOpen(false)} />
      </ModalComponent>

      <ModalComponent
        isOpen={isDowngradeModalOpen}
        onClose={() => setIsDowngradeModalOpen(false)}
        style={{ width: 'auto', maxWidth: '400px' }}
      >
        <div className={styles.warningModal}>
          <div className={styles.warningIcon}>⚠️</div>
          <h2 className={styles.warningTitle}>
            {pendingTier === 0
              ? _l('Attention: Cancellation confirmation', 'Atenção: Confirmação de Cancelamento', 'Atención: Confirmación de cancelación')
              : _l('Attention: Downgrade confirmation', 'Atenção: Confirmação de Downgrade', 'Atención: Confirmación de degradación')}
          </h2>
          <p className={styles.warningText}>
            {pendingTier === 0
              ? _l(
                'Your subscription cancellation will be scheduled for the end of the current cycle. Until then, you will keep full access to the active plan. After the renewal date, your account will return to the Free plan.',
                'O cancelamento da sua assinatura será agendado para o final do ciclo atual. Até lá, você manterá acesso completo ao plano vigente. Após a data de renovação, sua conta retornará para o plano Grátis.',
                'La cancelación de tu suscripción se programará para el final del ciclo actual. Hasta entonces, mantendrás acceso completo al plan activo. Después de la fecha de renovación, tu cuenta volverá al plan Gratuito.',
              )
              : _l(
                'Your downgrade will be scheduled for the end of the current cycle. You will keep full access to the current plan until then. The new plan charge will start on the next renewal.',
                'Seu downgrade será agendado para o final do ciclo atual. Você manterá acesso total ao plano atual até lá. A cobrança do novo plano começará na próxima renovação.',
                'Tu degradación se programará para el final del ciclo actual. Mantendrás acceso completo al plan actual hasta entonces. El cobro del nuevo plan comenzará en la próxima renovación.',
              )}
          </p>
          <div className={styles.warningActions}>
            <button
              className={styles.warningCancel}
              onClick={() => setIsDowngradeModalOpen(false)}
            >
              {_l('Cancel', 'Cancelar', 'Cancelar')}
            </button>
            <button
              className={styles.warningConfirm}
              onClick={handleConfirmDowngrade}
            >
              {pendingTier === 0
                ? _l('Yes, cancel subscription', 'Sim, cancelar assinatura', 'Sí, cancelar suscripción')
                : _l('Yes, schedule downgrade', 'Sim, agendar downgrade', 'Sí, programar degradación')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>
          {_l('Loading plan information...', 'Carregando informações do plano...', 'Cargando información del plan...')}
        </p>
      </div>
    );
  }

  if (isCheckoutUserVariant) {
    return (
      <div className={`${styles.container_page} ${styles.checkoutUserContainer}`}>
        <main className={styles.checkoutUserMain}>
          {pendingChangeBanner}
          {expiredBanner}

          <section className={styles.checkoutUserHero}>
            <motion.h1
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className={styles.checkoutUserTitle}
            >
              {checkoutUserCopy.title}
            </motion.h1>

            <motion.p
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={styles.checkoutUserHighlight}
            >
              {activeTab === CHECKOUT_USER_TABS.PACKAGES
                ? checkoutUserCopy.packagesHighlight
                : checkoutUserCopy.plansHighlight}
            </motion.p>

            {canShowPackagesTab && (
              <div className={styles.checkoutTabs}>
                <button
                  className={`${styles.checkoutTabButton} ${activeTab === CHECKOUT_USER_TABS.PLANS
                    ? styles.checkoutTabButton_active
                    : ''
                    }`}
                  onClick={() => setActiveTab(CHECKOUT_USER_TABS.PLANS)}
                >
                  {checkoutUserCopy.tabs.plans}
                </button>

                <button
                  className={`${styles.checkoutTabButton} ${activeTab === CHECKOUT_USER_TABS.PACKAGES
                    ? styles.checkoutTabButton_active
                    : ''
                    }`}
                  onClick={() => setActiveTab(CHECKOUT_USER_TABS.PACKAGES)}
                >
                  {checkoutUserCopy.tabs.packages}
                </button>
              </div>
            )}
          </section>

          {activeTab === CHECKOUT_USER_TABS.PACKAGES ? (
            <section
              className={`${styles.checkoutCardsGrid} ${checkoutCardsGridClassName}`}
            >
              {checkoutUserPackageCards.map((pkg, index) => (
                <motion.article
                  key={pkg.code}
                  initial={{ y: 28, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 * (index + 1) }}
                  className={`${styles.checkoutCard} ${pkg.featuredCard ? styles.checkoutCard_featured : ''
                    }`}
                >
                  <div className={styles.checkoutCardTop}>
                    <div
                      className={`${styles.checkoutIconBadge} ${pkg.featuredCard ? styles.checkoutIconBadge_featured : ''
                        }`}
                    >
                      <PackageCardSymbol highlighted={pkg.featuredCard} />
                    </div>
                  </div>

                  <div className={styles.checkoutCardIdentity}>
                    <h3 className={styles.checkoutCardTitle}>{pkg.title}</h3>
                    <p className={styles.checkoutCardSubtitle}>{pkg.subtitle}</p>
                  </div>

                  <div className={styles.checkoutPriceRow}>
                    <span className={styles.checkoutCardPrice}>{pkg.price}</span>
                  </div>

                  <button
                    onClick={() => handleSelectPackage(pkg.code)}
                    className={`${styles.checkoutSelectButton} ${pkg.featuredCard ? styles.checkoutSelectButton_featured : ''
                      }`}
                  >
                    {checkoutUserCopy.packageButton}
                  </button>

                  <div className={styles.checkoutDivider} />

                  <ul className={styles.checkoutFeatureList}>
                    {pkg.features.map((feature) => (
                      <li key={`${pkg.code}-${feature}`} className={styles.checkoutFeatureItem}>
                        <span className={styles.checkoutFeatureIcon}>
                          <CheckIcon
                            color={pkg.featuredCard ? '#6CFF8F' : '#53D769'}
                            size="13"
                          />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.article>
              ))}
            </section>
          ) : (
            <>
              <section
                className={`${styles.checkoutCardsGrid} ${checkoutCardsGridClassName}`}
              >
                {checkoutUserPlanCards.map((plan, index) => {
                  const isCurrentPlan =
                    currentTier === plan.tier && !isSubscriptionExpired;
                  const badgeLabel = isCurrentPlan
                    ? checkoutUserCopy.currentBadge
                    : plan.featuredCard
                      ? checkoutUserCopy.featuredBadge
                      : null;

                  return (
                    <motion.article
                      key={plan.id}
                      initial={{ y: 28, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.08 * (index + 1) }}
                      className={`${styles.checkoutCard} ${plan.featuredCard ? styles.checkoutCard_featured : ''
                        } ${isCurrentPlan ? styles.checkoutCard_current : ''}`}
                    >
                      <div className={styles.checkoutCardTop}>
                        <div
                          className={`${styles.checkoutIconBadge} ${plan.featuredCard ? styles.checkoutIconBadge_featured : ''
                            }`}
                        >
                          <PlanCardSymbol
                            iconKey={plan.iconKey}
                            highlighted={plan.featuredCard}
                          />
                        </div>

                        {badgeLabel && (
                          <span
                            className={`${styles.checkoutStatusPill} ${isCurrentPlan
                              ? styles.checkoutStatusPill_current
                              : styles.checkoutStatusPill_featured
                              }`}
                          >
                            {badgeLabel}
                          </span>
                        )}
                      </div>

                      <div className={styles.checkoutCardIdentity}>
                        <h3 className={styles.checkoutCardTitle}>{plan.name}</h3>
                        <p className={styles.checkoutCardSubtitle}>{plan.subtitle}</p>
                      </div>

                      <div className={styles.checkoutPriceRow}>
                        <span className={styles.checkoutCardPrice}>{plan.price}</span>
                        {plan.oldPrice && (
                          <span className={styles.checkoutCardOldPrice}>
                            {plan.oldPrice}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => handleSelectPlan(plan.tier)}
                        disabled={
                          isCurrentPlan &&
                          (!allowCurrentPlanSelection || plan.tier !== 0)
                        }
                        className={`${styles.checkoutSelectButton} ${plan.featuredCard ? styles.checkoutSelectButton_featured : ''
                          } ${isCurrentPlan &&
                            (!allowCurrentPlanSelection || plan.tier !== 0)
                            ? styles.checkoutSelectButton_disabled
                            : ''
                          }`}
                      >
                        {getCheckoutUserButtonText(plan)}
                      </button>

                      <div className={styles.checkoutDivider} />

                      <ul className={styles.checkoutFeatureList}>
                        {getCheckoutUserVisibleFeatures(plan.features).map((feature) => {
                          const [label, value] = feature.split(': ');
                          return (
                            <li key={`${plan.id}-${feature}`} className={styles.checkoutFeatureItem}>
                              <span className={styles.checkoutFeatureIcon}>
                                <CheckIcon
                                  color={plan.featuredCard ? '#6CFF8F' : '#53D769'}
                                  size="13"
                                />
                              </span>
                              <span>
                                {label}: <strong>{value}</strong>
                              </span>
                            </li>
                          );
                        })}
                        {plan.imageBank && (
                          <>
                            <li className={styles.checkoutFeatureItem}>
                              <span className={styles.checkoutFeatureIcon}>
                                <CheckIcon
                                  color={plan.featuredCard ? '#6CFF8F' : '#53D769'}
                                  size="13"
                                />
                              </span>
                              <span>
                                {_l('Image bank', 'Banco de imagens', 'Banco de imágenes')}:{' '}
                                <strong>{plan.imageBank.standard}</strong>
                              </span>
                            </li>
                            <li className={styles.checkoutFeatureItem}>
                              <span className={styles.checkoutFeatureIcon}>
                                <CheckIcon
                                  color={plan.featuredCard ? '#6CFF8F' : '#53D769'}
                                  size="13"
                                />
                              </span>
                              <span>
                                {_l('AI image bank', 'Banco de imagens por IA', 'Banco de imágenes por IA')}:{' '}
                                <strong>{plan.imageBank.ia}</strong>
                              </span>
                            </li>
                          </>
                        )}
                      </ul>
                    </motion.article>
                  );
                })}
              </section>

              {freePlan && (
                <section
                  className={`${styles.checkoutFreeCard} ${currentTier === 0 && !isSubscriptionExpired
                    ? styles.checkoutFreeCard_current
                    : ''
                    }`}
                >
                  <div className={styles.checkoutFreeHeader}>
                    <div className={styles.checkoutFreeIntro}>
                      <div className={styles.checkoutFreeTitleRow}>
                        <h3 className={styles.checkoutFreeTitle}>
                          {checkoutUserCopy.freeTitle}
                        </h3>
                        {currentTier === 0 && !allowCurrentPlanSelection && (
                          <span className={styles.checkoutFreeCurrentBadge}>
                            {checkoutUserCopy.currentBadge}
                          </span>
                        )}
                      </div>
                      <p className={styles.checkoutFreeDescription}>
                        {freePlan.description}
                      </p>
                    </div>

                    <div className={styles.checkoutFreePriceArea}>
                      <div className={styles.checkoutFreePriceRow}>
                        <span className={styles.checkoutFreePrice}>{freePlan.price}</span>
                      </div>

                      <button
                        onClick={() => handleSelectPlan(freePlan.tier)}
                        disabled={
                          currentTier === freePlan.tier &&
                          (!allowCurrentPlanSelection || freePlan.tier !== 0)
                        }
                        className={`${styles.checkoutSelectButton} ${styles.checkoutFreeButton} ${currentTier === freePlan.tier &&
                          (!allowCurrentPlanSelection || freePlan.tier !== 0)
                          ? styles.checkoutSelectButton_disabled
                          : ''
                          }`}
                      >
                        {getCheckoutUserButtonText(freePlan)}
                      </button>
                    </div>
                  </div>

                  <div className={styles.checkoutFreeList}>
                    {checkoutUserFreeItems.map((item) => (
                      <div
                        key={item.text}
                        className={`${styles.checkoutFreeListItem} ${item.tone === 'positive'
                          ? styles.checkoutFreeListItem_positive
                          : styles.checkoutFreeListItem_negative
                          }`}
                      >
                        <span
                          className={`${styles.checkoutFreeListIcon} ${item.tone === 'positive'
                            ? styles.checkoutFreeListIcon_positive
                            : styles.checkoutFreeListIcon_negative
                            }`}
                        >
                          {item.tone === 'positive' ? (
                            <CheckIcon color="#53D769" size="12" />
                          ) : (
                            '×'
                          )}
                        </span>
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          <section className={styles.checkoutRulesSection}>
            <h2 className={styles.checkoutRulesTitle}>
              {checkoutUserCopy.rulesTitle}
            </h2>

            <div className={styles.checkoutRulesList}>
              {checkoutUserRules.map((rule, index) => {
                const isOpen = openRuleIndex === index;

                return (
                  <button
                    key={rule.title}
                    className={`${styles.checkoutRuleItem} ${isOpen ? styles.checkoutRuleItem_open : ''
                      }`}
                    onClick={() =>
                      setOpenRuleIndex(isOpen ? null : index)
                    }
                    type="button"
                  >
                    <div className={styles.checkoutRuleHeader}>
                      <div className={styles.checkoutRuleTitleWrap}>
                        <InfoCircleIcon size="20" />
                        <span className={styles.checkoutRuleTitle}>
                          {rule.title}
                        </span>
                      </div>

                      <span
                        className={`${styles.checkoutRuleChevron} ${isOpen ? styles.checkoutRuleChevron_open : ''
                          }`}
                      >
                        <ChevronDown size="16" color="#4B5563" />
                      </span>
                    </div>

                    {isOpen && (
                      <p className={styles.checkoutRuleContent}>{rule.content}</p>
                    )}
                  </button>
                );
              })}
            </div>

            {(showBackButton || showCancelAccount) && (
              <div className={styles.checkoutUserActions}>
                {showBackButton && (
                  <button
                    className={styles.checkoutGhostButton}
                    onClick={() => navigate(backUrl || '/')}
                  >
                    {_l('Back', 'Voltar', 'Atrás')}
                  </button>
                )}

                {showCancelAccount && (
                  <button
                    className={styles.checkoutDangerButton}
                    onClick={() => setIsCancelModalOpen(true)}
                  >
                    {_l('Cancel account', 'Cancelar conta', 'Cancelar cuenta')}
                  </button>
                )}
              </div>
            )}

            <div className={styles.checkoutSecurity}>
              <span className={styles.checkoutSecurityIcon}>
                <LockClosed color="#C5B5DB" />
              </span>
              <span className={styles.checkoutSecurityText}>
                {checkoutUserCopy.securePayment} <strong>stripe</strong>
              </span>
            </div>
          </section>
        </main>

        {checkoutModals}
      </div>
    );
  }

  return (
    <div className={styles.container_page}>
      <main className={styles.main}>
        <div className={styles.hero}>
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className={styles.heroTitle}
          >
            {t('pricing.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={styles.heroSubtitle}
          >
            {t('pricing.hero_subtitle')}
          </motion.p>
        </div>

        {pendingChangeBanner}
        {expiredBanner}

        <div className={styles.plansGrid}>
          {paidPlans.map((plan, index) => {
            const isCurrentPlan = currentTier === plan.tier && !isSubscriptionExpired;

            return (
              <motion.div
                key={plan.id}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className={getCardClass(plan)}
              >
                {isCurrentPlan && !allowCurrentPlanSelection && (
                  <span className={styles.currentBadge}>
                    {_l('Current plan', 'Seu plano atual', 'Plan actual')}
                  </span>
                )}
                {plan.badge && !isCurrentPlan && (
                  <span className={styles.badge}>{plan.badge}</span>
                )}

                <div className={styles.planHeader}>
                  <h3 className={styles.planName}>{plan.name}</h3>
                  <div className={styles.planPriceContainer}>
                    {plan.oldPrice && (
                      <div className={styles.planOldPriceBox}>
                        <span className={styles.deLabel}>
                          {_l('From', 'De', 'Desde')}
                        </span>
                        <span className={styles.planOldPrice}>{plan.oldPrice}</span>
                      </div>
                    )}
                    <div className={styles.planPriceBox}>
                      <span className={styles.porLabel}>
                        {_l('For', 'Por', 'Por')}
                      </span>
                      <span className={styles.planPrice}>{plan.price}</span>
                      <span className={styles.planPeriod}>/{plan.period}</span>
                    </div>
                  </div>
                </div>

                <ul className={styles.featuresList}>
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className={styles.featureItem}>
                      <div className={styles.checkIcon}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.8566 8.19113C14.1002 7.85614 14.0261 7.38708 13.6911 7.14345C13.3561 6.89982 12.8871 6.97388 12.6434 7.30887L9.15969 12.099L7.28033 10.2197C6.98744 9.92678 6.51256 9.92678 6.21967 10.2197C5.92678 10.5126 5.92678 10.9874 6.21967 11.2803L8.71967 13.7803C8.87477 13.9354 9.08999 14.0149 9.30867 13.9977C9.52734 13.9805 9.72754 13.8685 9.85655 13.6911L13.8566 8.19113Z"
                            fill="#00B828"
                          />
                        </svg>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.tier)}
                  disabled={
                    isCurrentPlan &&
                    (!allowCurrentPlanSelection || plan.tier !== 0)
                  }
                  className={getButtonClass(plan)}
                >
                  {getButtonText(plan)}
                </button>
              </motion.div>
            );
          })}
        </div>

        {freePlan && (
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={`${styles.freeSection} ${currentTier === 0 ? styles.freeSectionCurrent : ''
              }`}
          >
            <div className={styles.freeSectionHeader}>
              <div className={styles.freeIdentity}>
                <div className={styles.freeTitleRow}>
                  <h3 className={styles.freeName}>{freePlan.name}</h3>
                  {currentTier === 0 && !allowCurrentPlanSelection && (
                    <span className={styles.freeCurrentBadge}>
                      {_l('Current plan', 'Seu plano atual', 'Plan actual')}
                    </span>
                  )}
                </div>
                <p className={styles.freeDescription}>{freePlan.description}</p>
              </div>

              <div className={styles.freePriceBox}>
                <span className={styles.freePriceLabel}>
                  {_l('For', 'Por', 'Por')}
                </span>
                <span className={styles.freePrice}>{freePlan.price}</span>
                <span className={styles.freePeriod}>/{freePlan.period}</span>
              </div>
            </div>

            <div className={styles.freeSectionBody}>
              <ul className={styles.freeFeaturesList}>
                {freePlan.features.map((feature, index) => (
                  <li
                    key={index}
                    className={`${styles.freeFeatureItem} ${feature.tone === 'positive'
                      ? styles.freeFeatureItem_positive
                      : styles.freeFeatureItem_negative
                      }`}
                  >
                    <div className={feature.tone === 'positive' ? styles.freeCheckIcon : styles.freeCrossIcon}>
                      {feature.tone === 'positive' ? (
                        <CheckIcon size="12" />
                      ) : (
                        <span style={{ fontSize: '14px', color: '#d55a83', fontWeight: 'bold', lineHeight: '1' }}>×</span>
                      )}
                    </div>
                    {feature.text}
                  </li>
                ))}
              </ul>

              <div className={styles.freeActionArea}>
                <button
                  onClick={() => handleSelectPlan(freePlan.tier)}
                  disabled={
                    currentTier === freePlan.tier &&
                    (!allowCurrentPlanSelection || freePlan.tier !== 0)
                  }
                  className={`${styles.selectButton} ${styles.freeButton} ${getButtonClass(freePlan)}`}
                >
                  {getButtonText(freePlan)}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {(showBackButton || showCancelAccount) && (
          <div className={styles.cancelSection}>
            {showBackButton && (
              <button
                className={styles.backButton}
                onClick={() => navigate(backUrl || '/')}
              >
                {_l('Back', 'Voltar', 'Atrás')}
              </button>
            )}
            {showCancelAccount && (
              <button
                className={styles.cancelButton}
                onClick={() => setIsCancelModalOpen(true)}
              >
                {_l('Cancel account', 'Cancelar Conta', 'Cancelar cuenta')}
              </button>
            )}
          </div>
        )}
      </main>

      {checkoutModals}
    </div>
  );
}

export default PlansSelectionView;
